import { AppId, FCX, usePlatformContext, usePlatformFeatureFlag } from "@datassential/platform-ui-lib";
import { HomeRankingTabEnum } from "@enums";
import HomeSectionHeader from "@pages/Home/components/HomeSectionHeader";
import HomeSectionTitle from "@pages/Home/components/HomeSectionTitle";
import HomeSectionDelimiter from "@pages/Home/components/HomeSectionDelimiter";
import HomeSectionLoader from "@pages/Home/components/HomeSectionLoader";
import HomeSectionTabs from "@pages/Home/components/HomeSectionTabs";
import { HOME_TOP_TABS } from "@pages/Home/constants";
import HomeTrendingTable from "./HomeTrendingTable";
import useLocalStorageState from "@hooks/useLocalStorageState";
import SelectFlavorCategories from "@pages/Home/sections/HomeTrending/SelectFlavorCategories";
import { useCallback, useMemo } from "react";
import { useHomeAllWhatsHotData } from "@apiHooks/useHomeAllWhatsHotData";
import HomeSectionFooter from "@pages/Home/components/HomeSectionFooter";
import HomeSectionFooterLink from "@pages/Home/components/HomeSectionFooterLink";


const HomeTrending: FCX = () => {
    const { isLoading, data: { data: dataRaw } } = useHomeAllWhatsHotData();
    const { userInfo: { products } } = usePlatformContext();
    const [selectedSubCategoriesIdsRaw, setSelectedSubCategoriesIdsRaw] = useLocalStorageState<string>(
        'home-whats-hot-subcategories',
        '',
    );
    const [activeTab, setActiveTab] = useLocalStorageState<HomeRankingTabEnum>('home-whats-hot-tab', HomeRankingTabEnum.WHATS_GROWING);
    const isSubCategoriesEnabled = usePlatformFeatureFlag('SNAP Whats Hot Page');

    const selectedSubCategoriesIds = useMemo(
        () => {
            if (!isSubCategoriesEnabled || selectedSubCategoriesIdsRaw.trim() === '') return [];
            return selectedSubCategoriesIdsRaw.split(',').map(id => Number(id));
        },
        [isSubCategoriesEnabled, selectedSubCategoriesIdsRaw]
    );

    const setSelectedSubCategoriesIds = useCallback(
        (ids: number[]) => {
            setSelectedSubCategoriesIdsRaw(ids.join(','));
        },
        [setSelectedSubCategoriesIdsRaw]
    );

    const filteredData = useMemo(
        () => {
            if (selectedSubCategoriesIds.length === 0) {
                return dataRaw;
            }
            return dataRaw?.filter(item =>
                item.subCategoriesIds?.some(id => selectedSubCategoriesIds.includes(id))
            );
        },
        [dataRaw, selectedSubCategoriesIds]
    );

    const sortedData = useMemo(
        () => {
            if (!filteredData) return [];

            const getRankingScore = (item: typeof filteredData[number]) => {
                const topScore = item.mentions ?? -Infinity;
                const growingScore = item.twelveMonthEngagementGrowth ?? -Infinity;

                return activeTab === HomeRankingTabEnum.WHATS_TOP
                    ? topScore
                    : growingScore;
            }

            return [...filteredData].sort(
                (a, b) => getRankingScore(b) - getRankingScore(a)
            );
        },
        [filteredData, activeTab]
    );

    const data = useMemo(
        () => sortedData.slice(0.50),
        [sortedData]
    );

    const trendingNow = useMemo(
        () => products[AppId.TrendingNow],
        [products]
    );

    return (
        <div className="Home__wrapper">
            <HomeSectionHeader>
                <div style={{ display: "flex", alignItems: "center", gap: '16px' }}>
                    <HomeSectionTitle>Trending Now</HomeSectionTitle>
                    {isSubCategoriesEnabled && (
                        <SelectFlavorCategories
                            value={selectedSubCategoriesIds}
                            setValue={setSelectedSubCategoriesIds}
                        />
                    )}
                </div>
                <div>
                    <HomeSectionTabs
                        data={HOME_TOP_TABS}
                        activeTabId={activeTab}
                        onItemClick={(id) => setActiveTab(id as HomeRankingTabEnum)}
                    />
                </div>
            </HomeSectionHeader>
            <div className="Home__sub-wrapper min-height">
                {isLoading ? (
                    <HomeSectionLoader />
                ) : (
                    <HomeTrendingTable
                        data={data}
                        isGrowingActive={activeTab === HomeRankingTabEnum.WHATS_GROWING}
                    />
                )}
                <HomeSectionFooter style={{ marginTop: -40 }}>
                    <HomeSectionFooterLink
                        url={trendingNow?.url || ''}
                        text="More Social Trends & MAC"
                        onContactUsClick={() => {}}
                        testId="linkHomeTrending"
                    />
                </HomeSectionFooter>
            </div>
            <HomeSectionDelimiter colors={['#1DFEEC', '#4FB0F9', '#FF59B9', '#FF0395']}/>
        </div>
    );
}

export default HomeTrending;